<script setup lang="ts">
  import { onMounted, ref, watch } from 'vue'
  import { useRouter } from 'vue-router'
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField, { type Option } from '@/components/admin/form/SelectField.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { useFeatureFlag } from '@/composables/useFeatureFlag'
  import {
    useLocaleStore,
    preferredLanguageToLocale,
    supportedLocales,
    type Locale
  } from '@/store/localStore'
  import {
    UsersService,
    type CustomerUser as User,
    type PatchedCustomerUserRequest,
    PreferredLanguageEnum,
    FlagEnum
  } from '@/types/generated'
  import { ROUTES } from '@/router/routes'

  const { t } = useTranslation({
    en: {
      profileSettings: 'Profile Settings',
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      phone: 'Phone',
      avatar: 'Profile Picture',
      changePassword: 'Change Password',
      preferredLanguage: 'Preferred Language',
      useOrganizationDefault: 'Use organization default'
    },
    de: {
      profileSettings: 'Profiloptionen',
      firstName: 'Vorname',
      lastName: 'Nachname',
      email: 'E-Mail',
      phone: 'Telefon',
      avatar: 'Profilbild',
      changePassword: 'Passwort ändern',
      preferredLanguage: 'Bevorzugte Sprache',
      useOrganizationDefault: 'Organisationsstandard verwenden'
    },
    fr: {
      profileSettings: 'Paramètres du profil',
      firstName: 'Prénom',
      lastName: 'Nom',
      email: 'E-mail',
      phone: 'Numéro de téléphone',
      avatar: 'Photo de profil',
      changePassword: 'Changer le mot de passe',
      preferredLanguage: 'Langue préférée',
      useOrganizationDefault: "Utiliser la langue par défaut de l'organisation"
    }
  })

  const router = useRouter()
  const user = ref<User>()
  const localeStore = useLocaleStore()
  const enableUserLanguagePreference = useFeatureFlag(FlagEnum.ENABLE_USER_LANGUAGE_PREFERENCE)

  // Language names in their native language (best practice for language selectors)
  const languageOptions: Option[] = [
    { value: null, title: t('useOrganizationDefault') },
    { value: PreferredLanguageEnum.DE, title: 'Deutsch' },
    { value: PreferredLanguageEnum.FR, title: 'Français' },
    { value: PreferredLanguageEnum.EN, title: 'English' }
  ]

  onMounted(async () => {
    user.value = (await UsersService.usersRetrieve({ uuid: 'current' })) as User
    await enableUserLanguagePreference.checkFlag()
  })

  // Watch for changes to preferred_language and update the locale store
  watch(
    () => user.value?.preferred_language,
    (newLanguage) => {
      if (newLanguage) {
        const locale = preferredLanguageToLocale[newLanguage as PreferredLanguageEnum]
        if (locale && supportedLocales.includes(locale)) {
          localeStore.setLocale(locale)
        }
      }
      // If null (use organization default), detect browser language or use default
      else if (newLanguage === null) {
        // Detect browser language and use it if supported
        const browserLang = navigator.language.split('-')[0] as Locale
        const fallbackLocale: Locale = supportedLocales.includes(browserLang) ? browserLang : 'en'
        localeStore.setLocale(fallbackLocale)
      }
    }
  )

  const handleSave = async (values: Partial<User>) => {
    await UsersService.usersPartialUpdate({
      uuid: 'current',
      requestBody: values as PatchedCustomerUserRequest
    })
  }
</script>

<template>
  <SettingsPage>
    <AutoSavingForm
      v-if="user"
      @save="handleSave"
    >
      <div class="mb-3 block font-medium">{{ t('profileSettings') }}</div>

      <InputField
        v-model="user.first_name"
        name="first_name"
        :label="t('firstName')"
      />

      <InputField
        v-model="user.last_name"
        name="last_name"
        :label="t('lastName')"
      />

      <InputField
        v-model="user.email"
        name="email"
        type="email"
        :label="t('email')"
        disabled
      />

      <InputField
        v-model="user.phone"
        name="phone"
        type="tel"
        :label="t('phone')"
      />

      <SelectField
        v-if="enableUserLanguagePreference.isEnabled.value"
        v-model="user.preferred_language"
        name="preferred_language"
        :label="t('preferredLanguage')"
        :options="languageOptions"
      />

      <div class="mt-6">
        <a
          :href="ROUTES.APP.SETTINGS.USER.PASSWORD"
          class="inline-block rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
          @click.prevent="router.push(ROUTES.APP.SETTINGS.USER.PASSWORD)"
        >
          {{ t('changePassword') }}
        </a>
      </div>
    </AutoSavingForm>
  </SettingsPage>
</template>
